import React from "react";
import CustomTypography from "../common/form/CustomTypography";
import AppTableRowCell from "../common/pages/AppTableRowCell";
import AppTableHeaderCell from "../common/pages/AppTableHeaderCell";
import {
  Box,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableContainer,
  TableSortLabel,
  useTheme
} from "@mui/material";
import AppTablePagenation from "../common/pages/AppTablePagenation";
import { SortType } from "../../../models/SortType";
import { Shipments } from "../../../models/Shipments";
import { DateConverter } from "../../../utils/DateConverter";

interface DashboardShipmentsTableProps {
  shipments: Shipments[];
  maxHeight: number;
  minHeight?: number;
  limit: number;
  page_no: number;
  total_pages: number;
  total_count: number;
  onPageChange: (page: number) => void;
  sortBy: string;
  sortType: SortType;
  onSortChange: (column: string, sortType: SortType) => void;
  onRowsPerPageChange: (limit: number) => void;
}
 
const DashboardShipmentsTable: React.FC<DashboardShipmentsTableProps> = (
  props: DashboardShipmentsTableProps
) => {
  const handleChangePage = (event: unknown, newPage: number) => {
    props.onPageChange(newPage);
  };
  const theme = useTheme();
  const handleRowsChange = (value: number) => {
    props.onPageChange(1);
    props.onRowsPerPageChange(value);
  };

  const renderSortIcon = (property: string) => {
    if (props.sortBy === property) {
      return props.sortType === SortType.ASC ? "↑" : "↓";
    }
    return "↑↓";
  };

  return (
    <Box sx={{ marginTop: 4 }}>
      <Box
        sx={{
          backgroundColor:theme.palette.customColors.white,
          padding: 4,
          borderRadius: 2,
          marginBottom: 2,
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 4 }}>
          <CustomTypography variant="heading4">Shipment List</CustomTypography>
        </Box>

        <TableContainer sx={{ maxHeight: props.maxHeight }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <AppTableHeaderCell
                  sx={{ color:theme.palette.customColors.tableGrayHeadingColor, fontWeight: "bold" }}
                >
                  <TableSortLabel
                    active={props.sortBy === "id"}
                    direction={props.sortType === SortType.ASC ? "asc" : "desc"}
                    onClick={() =>
                      props.onSortChange(
                        "id",
                        props.sortBy === "id" &&
                          props.sortType === SortType.DESC
                          ? SortType.ASC
                          : SortType.DESC
                      )
                    }
                    IconComponent={() => <span>{renderSortIcon("id")}</span>}
                    sx={{ fontSize: "12px" }}
                  >
                    SHIPMENT ID
                  </TableSortLabel>
                </AppTableHeaderCell>
                <AppTableHeaderCell
                  minWidth="200px"
                  sx={{ color:theme.palette.customColors.tableGrayHeadingColor, fontWeight: "bold", maxWidth: 400 }}
                >
                  PARTNER
                </AppTableHeaderCell>
                <AppTableHeaderCell
                  minWidth="200px"
                  sx={{ color: "#93969B", fontWeight: "bold", maxWidth: 400 }}
                >
                  LABEL
                </AppTableHeaderCell>
                <AppTableHeaderCell
                  sx={{ color:theme.palette.customColors.tableGrayHeadingColor, fontWeight: "bold" }}
                >
                  STATUS
                </AppTableHeaderCell>
                <AppTableHeaderCell
                  sx={{ color:theme.palette.customColors.tableGrayHeadingColor, fontWeight: "bold" }}
                >
                  <TableSortLabel
                    active={props.sortBy === "created_date"}
                    direction={props.sortType === SortType.ASC ? "asc" : "desc"}
                    onClick={() =>
                      props.onSortChange(
                        "created_date",
                        props.sortBy === "created_date" &&
                          props.sortType === SortType.DESC
                          ? SortType.ASC
                          : SortType.DESC
                      )
                    }
                    IconComponent={() => (
                      <span>{renderSortIcon("created_date")}</span>
                    )}
                    sx={{ fontSize: "12px" }}
                  >
                    CREATED DATE
                  </TableSortLabel>
                </AppTableHeaderCell>
                <AppTableHeaderCell
                  sx={{ color:theme.palette.customColors.tableGrayHeadingColor, fontWeight: "bold" }}
                >
                  <TableSortLabel
                    active={props.sortBy === "dispatch_date"}
                    direction={props.sortType === SortType.ASC ? "asc" : "desc"}
                    onClick={() =>
                      props.onSortChange(
                        "dispatch_date",
                        props.sortBy === "dispatch_date" &&
                          props.sortType === SortType.DESC
                          ? SortType.ASC
                          : SortType.DESC
                      )
                    }
                    IconComponent={() => (
                      <span>{renderSortIcon("dispatch_date")}</span>
                    )}
                    sx={{ fontSize: "12px" }}
                  >
                    DISPATCH TAT
                  </TableSortLabel>
                </AppTableHeaderCell>
                <AppTableHeaderCell
                  sx={{ color:theme.palette.customColors.tableGrayHeadingColor, fontWeight: "bold" }}
                >
                  DELIVERY DATE
                </AppTableHeaderCell>
                <AppTableHeaderCell
                  sx={{ color:theme.palette.customColors.tableGrayHeadingColor, fontWeight: "bold" }}
                >
                  DELIVERY TAT
                </AppTableHeaderCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {props.shipments.map((row) => (
                <TableRow key={row.id}>
                  <AppTableRowCell
                    sx={{
                      color:theme.palette.customColors.purple,  
                      cursor: "pointer",
                      fontWeight: "bold",
                    }}
                  >
                    {row.id}
                  </AppTableRowCell>
                  <AppTableRowCell>{row.customer_name}</AppTableRowCell>
                  <AppTableRowCell
                    minWidth="200px"
                    sx={{
                      fontSize: "0.8rem",
                      maxWidth: 300,
                      wordWrap: "break-word",
                      whiteSpace: "normal",
                    }}
                  >
                    {row.packs_label}
                  </AppTableRowCell>
                  <AppTableRowCell sx={{ fontSize: "0.8rem" }}>
                    <span
                      style={{
                        backgroundColor:
                          row.status === 3 || row.status === 4
                            ? theme.palette.lavenderBlue.main
                            : row.status === 1 || row.status === 2
                            ? theme.palette.blushPink.main
                            : row.status === 5
                            ? theme.palette.brown.main
                            : "transparent",
                        borderRadius: "17px",
                        padding: "3px 3px",
                        display: "inline-flex",
                        justifyContent: "center",
                        minWidth: "120px",
                      }}
                    >
                      {row.status_label}
                    </span>
                  </AppTableRowCell>

                  <AppTableRowCell sx={{ fontSize: "0.8rem" }}>
                    {DateConverter(row.created_date)}
                  </AppTableRowCell>
                  <AppTableRowCell sx={{ fontSize: "0.8rem" }}>
                    {DateConverter(row.dispatch_date)}
                  </AppTableRowCell>
                  <AppTableRowCell sx={{ fontSize: "0.8rem" }}>
                    {DateConverter(row.expected_delivery_date)}
                  </AppTableRowCell>
                  <AppTableRowCell sx={{ fontSize: "0.8rem" }}>
                    -
                  </AppTableRowCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <AppTablePagenation
          fontSize="0.8rem"
          pageNo={props.page_no || 1}
          limit={props.limit || 0}
          totalCount={props.total_count || 0}
          onPageChange={handleChangePage}
          handleRowsChange={handleRowsChange}
        />
      </Box>
    </Box>
  );
};

export default DashboardShipmentsTable;
